import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Button, Layout, Select } from "antd";
import "@progress/telerik-react-report-viewer/dist/dependencies/telerikReportViewer.kendo.min.js";
import "@progress/kendo-ui/css/web/kendo.common.min.css";
import "@progress/kendo-ui/css/web/kendo.default-v2.min.css";
import { TelerikReportViewer } from "@progress/telerik-react-report-viewer";
import { useReportsStore } from "../stores/context";
import { Content } from "antd/lib/layout/layout";
import { endpoints } from "../../../config";
import ReportParametersDialog from "../components/ReportParametersDialog";

const ReportPage: React.FC = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let viewer: any;
    const [selectedReport, setSelectedReport] = useState<string | undefined>(
        undefined,
    );
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [isReportParametersDialogVisible, setReportParametersDialogVisible] =
        useState(false);
    const [headerHeight, setHeaderHeight] = useState<number>(180);
    const headerRef = useRef<HTMLDivElement>(null);
    const store = useReportsStore().store;

    useEffect(() => {
        const calculateHeight = () => {
            if (headerRef.current) {
                const headerHeight = headerRef.current.offsetHeight;
                setHeaderHeight(headerHeight + 150);
            }
        };

        calculateHeight();
        window.addEventListener("resize", calculateHeight);

        return () => {
            window.removeEventListener("resize", calculateHeight);
        };
    }, [selectedProjects]); // Recalculate when projects change

    const handleRunReport = () => {
        if (selectedReport && selectedProjects && viewer) {
            viewer.viewerObject.reportSource({
                report: store.getReport(selectedReport)?.source,
                parameters: {
                    ...store.parametersToSubmit,
                    ProjectId: selectedProjects.join(","),
                },
            });
        }
    };

    const handleReportChange = async (reportId: string) => {
        store.setParamereterToSubmit(null);
        setSelectedReport(reportId);
        const resp = await store.getClient();
        resp.map(async (r) => {
            // Client fetched
            await store.getParameters(
                r.clientId,
                store.getReport(reportId)?.source ?? "",
            );
            if (store.parametersMetadata.length > 0) {
                setReportParametersDialogVisible(true);
            }
        });
    };

    return (
        <Layout className="screen-size layout-with-table">
            <div className="header-wrapper">
                <div className="title-wrapper">
                    <div className="header-title">Reports</div>
                </div>
            </div>
            <Layout>
                <Content>
                    <div
                        className="report-viewer-container"
                        style={{ height: "100vh", width: "100%" }}
                    >
                        <div
                            ref={headerRef}
                            className="projects-list-container"
                            style={{ marginBottom: 10 }}
                        >
                            <Select
                                options={store.enabledReports.map((r) => ({
                                    label: r.title,
                                    value: r.id,
                                }))}
                                placeholder="Choose report"
                                style={{ width: 300, marginRight: 10 }}
                                loading={store.reportsLoading}
                                onChange={(r) => {
                                    handleReportChange(r);
                                    store.refreshAvailableProjects(r);
                                }}
                            />
                            <Select
                                options={store.availableProjects.map((p) => ({
                                    label: p.name,
                                    value: p.id,
                                }))}
                                placeholder="Choose project"
                                style={{ width: 300, marginRight: 10 }}
                                mode="multiple"
                                onChange={(p) => {
                                    setSelectedProjects(p ?? []);
                                }}
                            />
                            <Button
                                data-id="run-report"
                                size="middle"
                                key="submit"
                                type="primary"
                                onClick={() =>
                                    setReportParametersDialogVisible(true)
                                }
                                style={{ marginRight: 10 }}
                                disabled={
                                    !selectedReport ||
                                    store.parametersMetadata.length === 0
                                }
                            >
                                Parameters...
                            </Button>
                            <Button
                                data-id="run-report"
                                size="middle"
                                key="submit"
                                type="primary"
                                onClick={handleRunReport}
                                disabled={
                                    !selectedReport ||
                                    selectedProjects.length === 0 ||
                                    (!Object.keys(
                                        store.parametersToSubmit ?? {},
                                    ).length &&
                                        store.parametersMetadata.length > 0)
                                }
                            >
                                View report
                            </Button>
                        </div>
                        <TelerikReportViewer
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ref={(el: any) => (viewer = el)}
                            serviceUrl={endpoints.reportsUrL}
                            // reportSource={selectedSource}
                            viewerContainerStyle={{
                                position: "relative",
                                height: `calc(100vh - ${headerHeight}px)`,
                                width: "100%",
                                overflow: "hidden",
                                clear: "both",
                                fontFamily: "ms sans serif",
                            }}
                            viewMode="INTERACTIVE"
                            scaleMode="SPECIFIC"
                            scale={1.0}
                            enableAccessibility={false}
                            showDocumentMap={false}
                            viewerOptions={{
                                showDocumentMap: false,
                            }}
                        />
                    </div>
                    <ReportParametersDialog
                        store={store}
                        isVisible={isReportParametersDialogVisible}
                        parameters={store.parametersMetadata}
                        onCancel={() => setReportParametersDialogVisible(false)}
                    />
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(ReportPage);
